import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Crown, User, ArrowLeft, Users } from "lucide-react";

/**
 * Start Session — two-step picker.
 *
 * Step 1 — Admin picks Member or Non-Member.
 * Step 2 — If Member, admin picks Club Member vs Cross-Club Member. The
 *          selected sub-type is persisted on the session so billing uses the
 *          right rate at Stop time.
 *
 * The player's name and payment method are captured at STOP time — this keeps
 * the floor flow fast: tap Start → pick type → table is running.
 */
export default function StartSessionModal({
  open,
  table,
  onClose,
  onSubmit,
}) {
  const [step, setStep] = useState("type"); // "type" | "memberSubtype"

  // Reset to step 1 whenever the modal re-opens so a stale sub-picker
  // never blocks the primary flow.
  useEffect(() => {
    if (open) setStep("type");
  }, [open]);

  const start = (player_type) => {
    onSubmit({
      player_type,
      // Backend now accepts empty name and stores "Player" as a placeholder.
      // Name + payment are captured together in the Stop Session modal.
      player_name: "",
      player_id: null,
      save_player: false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="start-session-modal"
        className="bg-[#121a16] border border-white/10 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-black tracking-tight">
            Start Session
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            <span>{table?.name}</span>
            <span className="text-white/20 mx-2">·</span>
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-300">
              {step === "type" ? "Pick player type" : "Which kind of member?"}
            </span>
          </DialogDescription>
        </DialogHeader>

        {step === "type" ? (
          <div className="mt-3 space-y-3" data-testid="start-step-1">
            <div className="text-xs uppercase tracking-[0.25em] text-gray-400 font-bold">
              Who&apos;s playing?
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="start-as-member"
                onClick={() => setStep("memberSubtype")}
                className="group relative bg-black/40 border-2 border-white/10 hover:border-amber-400 hover:bg-amber-400/10 rounded-xl p-5 text-left transition flex flex-col items-start gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
              >
                <Crown className="w-7 h-7 text-amber-400 group-hover:scale-110 transition-transform" />
                <div>
                  <div className="font-black uppercase tracking-wider text-white">
                    Member
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Club or Cross-Club rate
                  </div>
                </div>
              </button>

              <button
                type="button"
                data-testid="start-as-non-member"
                onClick={() => start("non_member")}
                className="group relative bg-black/40 border-2 border-white/10 hover:border-emerald-400 hover:bg-emerald-400/10 rounded-xl p-5 text-left transition flex flex-col items-start gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
              >
                <User className="w-7 h-7 text-emerald-300 group-hover:scale-110 transition-transform" />
                <div>
                  <div className="font-black uppercase tracking-wider text-white">
                    Non-Member
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Standard walk-in rate
                  </div>
                </div>
              </button>
            </div>

            <p className="text-[11px] text-gray-500 leading-relaxed pt-2">
              Player name &amp; payment method will be asked when the session is stopped.
            </p>
          </div>
        ) : (
          <div className="mt-3 space-y-3" data-testid="start-step-member-subtype">
            <button
              type="button"
              onClick={() => setStep("type")}
              data-testid="start-member-subtype-back"
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>

            <div className="text-xs uppercase tracking-[0.25em] text-gray-400 font-bold pt-1">
              Which kind of member?
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="start-as-club-member"
                onClick={() => start("member")}
                className="group relative bg-black/40 border-2 border-white/10 hover:border-amber-400 hover:bg-amber-400/10 rounded-xl p-5 text-left transition flex flex-col items-start gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
              >
                <Crown className="w-7 h-7 text-amber-400 group-hover:scale-110 transition-transform" />
                <div>
                  <div className="font-black uppercase tracking-wider text-white">
                    Club Member
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Home club rate
                  </div>
                </div>
              </button>

              <button
                type="button"
                data-testid="start-as-cross-club-member"
                onClick={() => start("cross_club_member")}
                className="group relative bg-black/40 border-2 border-white/10 hover:border-sky-400 hover:bg-sky-400/10 rounded-xl p-5 text-left transition flex flex-col items-start gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60"
              >
                <Users className="w-7 h-7 text-sky-300 group-hover:scale-110 transition-transform" />
                <div>
                  <div className="font-black uppercase tracking-wider text-white">
                    Cross-Club Member
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Reciprocal rate
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="cancel-start-btn"
            className="text-gray-400 hover:bg-white/5"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
